import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, Facebook, Linkedin } from "lucide-react";
import { useT } from "@/context/LangContext";
import { useBrand } from "@/context/BrandContext";
import SafeEmail from "@/components/SafeEmail";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cercle-local-bhealth/artifacts/csfd9ojj_logofinal-logo-full-color-rgb-900px-w-72ppi.png";

export default function Footer() {
  const { t } = useT();
  const { brand } = useBrand();
  return (
    <footer className="bg-navy-900 text-white" data-testid="site-footer">
      <div className="container-app py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-12 w-12 rounded-2xl bg-white border border-white/10 p-1.5 flex items-center justify-center">
              <img src={LOGO_URL} alt="B.health®" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-lg uppercase tracking-wider">Local Benefits</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-semibold">by B.health<sup>®</sup></div>
            </div>
          </div>
          <p className="text-white/70 max-w-md leading-relaxed text-sm">{t("footer.description")}</p>
        </div>

        <div>
          <h4 className="text-sm uppercase tracking-wider text-lime-400 font-semibold mb-4">{t("footer.contact")}</h4>
          <ul className="space-y-3 text-sm text-white/80">
            <li className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 text-lime-400 shrink-0" /><span>{brand.address}</span></li>
            <li className="flex gap-2"><Phone className="w-4 h-4 mt-0.5 text-lime-400 shrink-0" /><a href={`tel:${brand.phone.replace(/\s/g,"")}`} className="hover:text-lime-400">{brand.phone}</a></li>
            <li className="flex gap-2"><Mail className="w-4 h-4 mt-0.5 text-lime-400 shrink-0" />{(() => { const [u, d] = (brand.email || "").split("@"); return u && d ? <SafeEmail user={u} domain={d} className="hover:text-lime-400" testId="footer-email" /> : <span>—</span>; })()}</li>
          </ul>
        </div>

        <div>
          <h4 className="text-sm uppercase tracking-wider text-lime-400 font-semibold mb-4">{t("footer.links")}</h4>
          <ul className="space-y-3 text-sm text-white/80">
            <li><Link to="/partenaires" className="hover:text-lime-400" data-testid="footer-link-partners">{t("footer.link.partners")}</Link></li>
            <li><Link to="/devenir-partenaire" className="hover:text-lime-400" data-testid="footer-link-become">{t("footer.link.become")}</Link></li>
            {brand.website && <li><a href={brand.website} target="_blank" rel="noreferrer" className="hover:text-lime-400">{brand.website.replace(/^https?:\/\//, "")}</a></li>}
            <li><Link to="/admin/login" className="hover:text-lime-400" data-testid="footer-link-admin">{t("footer.link.admin")}</Link></li>
          </ul>
          <div className="flex gap-3 mt-5">
            {brand.instagram && (
              <a aria-label="Instagram" href={brand.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-lime-500 hover:text-navy-900 flex items-center justify-center transition"><Instagram className="w-4 h-4" /></a>
            )}
            {brand.facebook && (
              <a aria-label="Facebook" href={brand.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-lime-500 hover:text-navy-900 flex items-center justify-center transition"><Facebook className="w-4 h-4" /></a>
            )}
            {brand.linkedin && (
              <a aria-label="LinkedIn" href={brand.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-white/10 hover:bg-lime-500 hover:text-navy-900 flex items-center justify-center transition"><Linkedin className="w-4 h-4" /></a>
            )}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-app py-5 flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-white/50">
          <span>© {new Date().getFullYear()} {brand.center_short} — {t("footer.rights")}</span>
          <span>{t("footer.disclaimer")}</span>
        </div>
        <div className="container-app pb-5 text-center text-[10px] tracking-widest uppercase text-white/25">
          {/* Prodinvideo watermark — required legal notice */}
          <a
            href={brand.legal_owner_url || "https://prodinvideo.be"}
            target="_blank"
            rel="noreferrer"
            className="hover:text-white/50 transition-colors"
            data-testid="prodinvideo-watermark"
          >
            {brand.copyright_text || "Concept propriétaire · Prodinvideo SPRL"}
          </a>
        </div>
      </div>
    </footer>
  );
}
